// The "critical tabs in a strip, everything else behind More" rule, shared by
// every screen that uses components/ui/PriorityTabNav.tsx (the IP Case Sheet,
// Patient Records). Pure, so it is unit-tested without a browser
// (frontend/tests/caseSheetTabs.test.mjs).
//
// ── Groups ───────────────────────────────────────────────────────
//
//   critical   eligible for the strip; shown in `priority` order while they
//              fit beside the More button, then overflow into More ("Core")
//   secondary  always in More
//
// Array order is DISPLAY order and never changes with width — a tab that fits
// stays where it was. `priority` only decides which critical tabs survive when
// space runs out (1 = last to leave).

export interface PriorityTab<Id extends string = string> {
  id: Id;
  label: string;
  group: 'critical' | 'secondary';
  /** Critical tabs only: lower survives longer at narrow widths. */
  priority?: number;
  /** Optional grant that hides the tab entirely (see visibleTabs). */
  module?: string;
}

/**
 * Which critical tabs fit in the strip.
 *
 * Take critical tabs by priority while each still fits beside the More button
 * (at the width it will show — plain "More", or "MORE / <section>" when the
 * active tab is in the menu). Stops at the first tab that doesn't fit, so
 * priority is strict. At least the top-priority tab always stays.
 */
export function fitStripTabs<Id extends string>(
  tabs: readonly PriorityTab<Id>[],
  widths: ReadonlyMap<Id, number>,
  available: number,
  moreWidth: number,
  gap: number,
): Set<Id> {
  const critical = tabs
    .filter(t => t.group === 'critical')
    .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  const hasMenuAnyway = tabs.some(t => t.group === 'secondary');
  const fitted = new Set<Id>();
  let used = 0;
  for (const t of critical) {
    const w = widths.get(t.id);
    if (w === undefined) continue;
    const next = used + (fitted.size ? gap : 0) + w;
    // Reserve More whenever something will be in it: secondary tabs, or the
    // critical tabs after this one.
    const needsMore = hasMenuAnyway || fitted.size + 1 < critical.length;
    const budget = needsMore ? available - moreWidth - gap : available;
    if (next > budget && fitted.size > 0) break;
    fitted.add(t.id);
    used = next;
  }
  return fitted;
}

/**
 * How the tabs split between the strip and the More menu once `fitted` is
 * known: critical tabs that did not fit ("Core", in display order), the
 * secondary tabs, and which of them — if any — is the active one, so the More
 * button can carry its name.
 */
export function menuGroups<Id extends string, T extends PriorityTab<Id>>(
  tabs: readonly T[],
  fitted: ReadonlySet<Id> | null,
  active: Id,
): { strip: T[]; core: T[]; secondary: T[]; activeInMenu: T | undefined } {
  const strip = tabs.filter(t => fitted?.has(t.id));
  const core = tabs.filter(t => t.group === 'critical' && fitted !== null && !fitted.has(t.id));
  const secondary = tabs.filter(t => t.group === 'secondary');
  const activeInMenu = [...core, ...secondary].find(t => t.id === active);
  return { strip, core, secondary, activeInMenu };
}
